<template>
    <LogoLink class="hidden xl:block" />
    <nav class="hidden xl:flex xl:flex-1 xl:items-center xl:gap-x-7">
        <div
            v-for="(menuItem, index) in data"
            :id="menuItem.key"
            :key="menuItem.key"
            class="relative"
        >
            <Menu v-slot="{ open, close }">
                <MenuButton
                    class="group flex w-full items-center justify-between py-3 font-semibold text-grind transition-all hover:text-brew"
                    :class="{
                        'mt-[2px] border-b-2 border-grind hover:border-brew':
                            open,
                    }"
                    @click.prevent="adjustMenuPosition(String(index), open)"
                >
                    <span class="mr-2 font-bold text-inherit">
                        {{ menuItem.title }}
                    </span>
                    <span
                        :class="{ 'lg:rotate-180': open }"
                        class="-rotate-90 lg:rotate-0"
                    >
                        <Icon
                            name="chevron-down"
                            class="text-sm text-inherit"
                        />
                    </span>
                </MenuButton>

                <FadeTransition>
                    <MenuItems
                        v-show="open"
                        :ref="
                            (el) => {
                                if (el) menuRefs[String(index)] = el;
                            }
                        "
                        static
                        :style="menuStyles[index]"
                        class="rounded-md bg-white px-6 py-5 shadow-md focus:outline-none lg:absolute lg:top-[4.25rem] lg:origin-top-left lg:drop-shadow-[0_0_3px_rgba(0,0,0,0.25)]"
                    >
                        <div class="flex gap-x-12">
                            <MenuItem
                                v-for="(group, groupIndex) in menuItem.groups"
                                :key="groupIndex"
                                as="ul"
                            >
                                <li
                                    v-if="
                                        menuItem.groups &&
                                        menuItem.groups.length - 1 ===
                                            groupIndex &&
                                        menuItem.buttons
                                    "
                                >
                                    <MenuItem
                                        v-for="buttonItem in menuItem.buttons"
                                        :key="buttonItem.label"
                                        as="div"
                                    >
                                        <Button
                                            :button="buttonItem"
                                            @click.prevent="close"
                                        />
                                    </MenuItem>
                                </li>
                                <MenuListItem
                                    :group="group"
                                    :index="groupIndex"
                                    @link-click="close"
                                />
                            </MenuItem>
                        </div>
                    </MenuItems>
                </FadeTransition>
            </Menu>
        </div>

        <!-- Other Nav Items -->
        <NuxtLink
            to="/coupon-code"
            class="group flex items-center whitespace-nowrap py-3 font-semibold text-grind text-vibe transition-all hover:text-brew"
        >
            <Icon name="left-accent-icon" class="text-xl" />
            <span class="font-accent text-xl">Deals</span>
            <Icon name="right-accent-icon" class="text-xl" />
        </NuxtLink>

        <div
            id="design-price-div"
            class="px-4 xl:flex xl:flex-1 xl:justify-end xl:px-0"
        >
            <ButtonContainer>
                <NuxtLink
                    class="btn-primary !inline-flex items-center gap-2"
                    to="/design-and-price"
                >
                    <Icon name="pencil" class="text-[20px]" />
                    Design & Price
                </NuxtLink>
            </ButtonContainer>
        </div>
    </nav>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import MenuListItem from '~/layouts/includes/menu/MenuListItem.vue';
import FadeTransition from '~/components/transitions/FadeTransition.vue';
import LogoLink from '~/layouts/includes/menu/LogoLink.vue';
import MenuType from '~/types/MenuType';
import Button from '~/components/page-building/components/Button.vue';

defineProps<{
    data: MenuType[];
}>();

const menuRefs = ref<Record<string, HTMLElement>>({});
const menuStyles = ref<Record<string, Record<string, string>>>({});
const adjustMenuPosition = async (key: string, open: boolean) => {
    await nextTick();
    const el = menuRefs.value[key];
    if (el?.$el) {
        const rect = el?.$el.getBoundingClientRect();
        const padding = 24;
        const distanceToRight = window.innerWidth - rect.right - padding;

        if (distanceToRight < 0) {
            menuStyles.value[key] = {
                left: `${distanceToRight}px`,
            };
        }
    }
};
</script>
